import * as React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { repository } from '../repositories/index.js';
import { useAuthSession } from '../features/auth/AuthSession.js';
import { CloseIcon, MenuIcon } from './Icons.js';
import { MockNotice } from './MockNotice.js';

interface NavigationItem {
  label: string;
  to: string;
}

const tenantNavigation: NavigationItem[] = [
  { label: 'Каталог', to: '/tenant/catalog?query=R28003' },
  { label: 'Поставщики', to: '/tenant/suppliers' },
  { label: 'Сопоставления', to: '/tenant/mappings' },
  { label: 'Пользователи и доступ', to: '/tenant/users' },
];

const platformNavigation: NavigationItem[] = [
  { label: 'Тенанты', to: '/platform/tenants' },
  { label: 'Запросы на доступ', to: '/platform/access-requests' },
  { label: 'Коммерческий доступ', to: '/platform/commercial-access' },
  { label: 'Интеграции поставщиков', to: '/platform/integrations' },
  { label: 'Доступ поддержки', to: '/platform/support-access' },
  { label: 'Доступ платформы', to: '/platform/platform-access' },
];

export type ShellVariant = 'tenant' | 'platform' | 'support';

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('') || 'DS';
}

export function Shell({ variant, children }: { variant: ShellVariant; children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const session = useAuthSession();
  const navigation = variant === 'platform' ? platformNavigation : tenantNavigation;
  const apiTenantContext = repository.mode === 'api' && variant === 'tenant' ? session.context : null;
  const contextLabel = apiTenantContext?.tenant.name ?? (variant === 'platform' ? 'Платформа DataSota' : 'Мособои');
  const identity = apiTenantContext
    ? `${apiTenantContext.tenantRoleLabel} · ${apiTenantContext.tenant.name}`
    : variant === 'tenant' ? 'Владелец · Мособои' : variant === 'support' ? 'Поддержка DataSota' : 'Администратор платформы';
  const avatar = apiTenantContext ? initials(apiTenantContext.user.displayName) : variant === 'tenant' ? 'АК' : variant === 'support' ? 'DS' : 'АП';

  return (
    <div className={`app-shell shell-${variant}`} data-shell={variant} data-read-only={variant === 'support' ? 'true' : 'false'} data-data-mode={repository.mode}>
      <header className="topbar">
        <div className="brand-group">
          <button className="mobile-menu-button icon-button" type="button" aria-label="Открыть навигацию" onClick={() => setMenuOpen(true)}>
            <MenuIcon />
          </button>
          <span className="brand">DataSota</span>
          <span className={`context-chip context-${variant}`}>{contextLabel}</span>
        </div>
        <div className="identity-block" data-testid="shell-identity">
          <span>{identity}</span>
          <span className="avatar" aria-hidden="true">{avatar}</span>
        </div>
      </header>

      {variant === 'support' ? (
        <div className="support-banner" data-testid="support-banner">
          <div>
            <strong>Режим поддержки · только чтение</strong>
            <span>Тенант: Мособои · Доступ действует до 12.08.2026 11:30</span>
          </div>
          <button type="button" className="button secondary support-exit" onClick={() => navigate('/platform/support-access')}>Выйти из режима</button>
        </div>
      ) : null}

      <div className="shell-body">
        <aside className={menuOpen ? 'sidebar mobile-open' : 'sidebar'} aria-label="Основная навигация">
          <div className="mobile-sidebar-head">
            <span className="brand">DataSota</span>
            <button className="icon-button" type="button" aria-label="Закрыть навигацию" onClick={() => setMenuOpen(false)}><CloseIcon /></button>
          </div>
          <nav>
            <span className="nav-eyebrow">{variant === 'platform' ? 'ПЛАТФОРМА' : variant === 'support' ? 'ТЕНАНТ · READ ONLY' : 'РАБОЧЕЕ ПРОСТРАНСТВО'}</span>
            {navigation.map((item) => {
              const targetPath = item.to.split('?')[0] ?? item.to;
              const supportTo = variant === 'support' ? item.to.replace('/tenant/', '/support/') : item.to;
              const activePath = variant === 'support' ? targetPath.replace('/tenant/', '/support/') : targetPath;
              return (
                <NavLink
                  key={item.label}
                  to={supportTo}
                  className={location.pathname === activePath ? 'nav-link active' : 'nav-link'}
                  onClick={() => setMenuOpen(false)}
                >
                  <span className="nav-marker" aria-hidden="true" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="sidebar-foot">
            <span>{variant === 'support' ? 'Контекст тенанта не меняет платформенную личность' : 'Источник прав: сервер DataSota'}</span>
          </div>
        </aside>
        {menuOpen ? <button type="button" className="sidebar-backdrop" aria-label="Закрыть навигацию" onClick={() => setMenuOpen(false)} /> : null}
        <main className="main-content" id="main-content">
          {repository.mode === 'mock' ? <MockNotice /> : null}
          {children}
        </main>
      </div>
    </div>
  );
}
